/**
 * @fileoverview Zustand store for slide-over panel state management
 *
 * Manages the open/close state of the slide-over panel used to display
 * item details without leaving the current page context. Supports optional
 * instance context for issue-specific curation workflows.
 */

import { create } from 'zustand';

/**
 * Slide-over panel state store
 *
 * @typedef {Object} SlideOverState
 * @property {boolean} isOpen - Whether the slide-over panel is currently open
 * @property {string|null} itemHash - Hash of the item currently being displayed
 * @property {string|null} returnPath - Path to return to when panel closes
 * @property {string[]} itemList - Array of item hashes for navigation
 * @property {string|null} instanceId - ID of the item instance (format: "hash:ulid") when viewing from issue
 * @property {string[]} instanceList - Array of instance IDs for navigation (parallel to itemList)
 * @property {(hash: string, returnPath?: string, itemList?: string[], instanceId?: string, instanceList?: string[]) => void} openSlideOver - Open panel with item
 * @property {() => void} closeSlideOver - Close the panel
 * @property {() => void} navigatePrevious - Navigate to previous item
 * @property {() => void} navigateNext - Navigate to next item
 * @property {() => {current: number, total: number} | null} getPosition - Get current position in list
 */

export const useSlideOverStore = create((set, get) => ({
  isOpen: false,
  itemHash: null,
  returnPath: null,
  itemList: [],
  instanceId: null,
  instanceList: [],

  /**
   * Open the slide-over panel with a specific item
   * @param {string} hash - Hash of the item to display
   * @param {string} [returnPath] - Optional path to return to when closed
   * @param {string[]} [itemList] - Optional array of item hashes for navigation
   * @param {string} [instanceId] - Optional instance ID when viewing from issue
   * @param {string[]} [instanceList] - Optional array of instance IDs for navigation
   */
  openSlideOver: (hash, returnPath = null, itemList = [], instanceId = null, instanceList = []) =>
    set({
      isOpen: true,
      itemHash: hash,
      returnPath,
      itemList,
      instanceId,
      instanceList,
    }),

  /**
   * Close the slide-over panel and reset state after animation completes
   */
  closeSlideOver: () => {
    // First, just set isOpen to false to trigger the slide-out animation
    set({ isOpen: false });

    // After animation completes (300ms), clear the rest of the state
    setTimeout(() => {
      set({
        itemHash: null,
        returnPath: null,
        itemList: [],
        instanceId: null,
        instanceList: [],
      });
    }, 350); // Slightly longer than animation duration (300ms) to ensure it completes
  },

  /**
   * Navigate to the previous item in the list
   */
  navigatePrevious: () => {
    const { itemHash, itemList, instanceList } = get();
    if (!itemList?.length) return;
    const currentIndex = itemList.indexOf(itemHash);
    if (currentIndex > 0) {
      const updates = { itemHash: itemList[currentIndex - 1] };
      // Also update instanceId if we have instance context
      if (instanceList?.length) {
        updates.instanceId = instanceList[currentIndex - 1] || null;
      }
      set(updates);
    }
  },

  /**
   * Navigate to the next item in the list
   */
  navigateNext: () => {
    const { itemHash, itemList, instanceList } = get();
    if (!itemList?.length) return;
    const currentIndex = itemList.indexOf(itemHash);
    if (currentIndex >= 0 && currentIndex < itemList.length - 1) {
      const updates = { itemHash: itemList[currentIndex + 1] };
      // Also update instanceId if we have instance context
      if (instanceList?.length) {
        updates.instanceId = instanceList[currentIndex + 1] || null;
      }
      set(updates);
    }
  },

  /**
   * Get current position in the item list
   * @returns {{current: number, total: number} | null}
   */
  getPosition: () => {
    const { itemHash, itemList } = get();
    if (!itemList?.length) return null;
    const currentIndex = itemList.indexOf(itemHash);
    if (currentIndex < 0) return null;
    return { current: currentIndex + 1, total: itemList.length };
  },
}));
